import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { isSupabaseConfigured } from './supabase';
import { showToast } from './components/Toast';

/**
 * Plan entitlements. Right now there is exactly one paid capability — embedding
 * photos in a project — and no billing yet, so "Pro" is granted manually by
 * setting the user's `app_metadata.plan = 'pro'` in Supabase. Because photos are
 * stored inline (base64) inside the project data, letting everyone add them
 * would bloat both the localStorage autosave and the cloud rows; gating keeps
 * free projects small and the storage bill at zero.
 *
 * Grant Pro (Supabase SQL editor):
 *   update auth.users
 *     set raw_app_meta_data = raw_app_meta_data || '{"plan":"pro"}'::jsonb
 *     where email = 'someone@example.com';
 * app_metadata is server-only, so users can't grant it to themselves.
 *
 * When Supabase isn't configured (local/desktop, or a pre-auth deploy) there is
 * no account system at all, so everything is unlocked — the gate only bites once
 * real accounts exist.
 */

/**
 * What a free (signed-in, non-Pro) account can do. Pro lifts all of these.
 * Kept in one place so the paywall messaging and the enforcement never drift.
 */
export const FREE_LIMITS = {
  /** Max projects saved to the cloud. */
  cloudProjects: 1,
  /** Max subsystems (folders of sheets) in a single project. */
  subsystems: 1,
} as const;

let pro = !isSupabaseConfigured;
const listeners = new Set<(v: boolean) => void>();

export function isPro(): boolean {
  return pro;
}

/** Recompute Pro from the current session. Called by the App's auth listener. */
export function setEntitlementsFromSession(session: Session | null): void {
  const appMeta = session?.user?.app_metadata as { plan?: string } | undefined;
  const userMeta = session?.user?.user_metadata as { plan?: string } | undefined;
  const next = !isSupabaseConfigured || appMeta?.plan === 'pro' || userMeta?.plan === 'pro';
  if (next === pro) return;
  pro = next;
  listeners.forEach((l) => l(pro));
}

/** React hook for components that need to show/hide Pro-only affordances. */
export function useIsPro(): boolean {
  const [v, setV] = useState(pro);
  useEffect(() => {
    setV(pro);
    listeners.add(setV);
    return () => {
      listeners.delete(setV);
    };
  }, []);
  return v;
}

/**
 * Guard a Pro-only action. Returns true if allowed; otherwise shows a toast and
 * returns false so the caller can bail without performing the action.
 */
export function requirePro(what = 'This feature'): boolean {
  if (pro) return true;
  showToast(`${what} is a Pro feature — coming to the Pro plan soon.`, 'info', 2600);
  return false;
}

/**
 * Show a paywall notice for a free-plan limit (distinct from requirePro, which
 * is for capabilities). Pass the full sentence, e.g. the project-count message.
 */
export function showProNotice(message: string): void {
  showToast(message, 'info', 3000);
}
